import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { DialogService } from './dialog.service';
import { DiagnosesService } from './diagnoses.service';

export interface LibraryContext {
  casename: string;
  currentFolder: string;
  currentFolderName: string;
  caseFolders: any[];
  userIsConsultant: boolean;
  userIsClient: boolean;
}

/**
 * Facade for Patient Library and Diagnoses dialog flows.
 * Extracted from ClientProfileComponent to reduce its complexity.
 * All methods that need component state receive it via LibraryContext.
 *
 * NOTE: showMyDiagnoses returns the MatDialogRef so the component can
 * subscribe to afterClosed() and call getFilesForPatient(), which is
 * not owned by this facade.
 */
@Injectable({
  providedIn: 'root'
})
export class ClientProfileLibraryFacade {
  constructor(
    private dialog_$: DialogService,
    private diagnoses_$: DiagnosesService
  ) {}

  /**
   * Opens the Library Manager dialog.
   */
  async showLibraryManager(ctx: LibraryContext): Promise<void> {
    const { LibraryManagementComponent } = await import('../components/library-management/library-management.component');
    this.dialog_$.open(LibraryManagementComponent, {
      height: '100vh',
      width: '100vw',
      maxWidth: '100vw',
      data: {
        casename: ctx.casename,
        folderId: ctx.currentFolder,
        folderName: ctx.currentFolderName,
        caseFolders: ctx.caseFolders,
        roleConsultant: ctx.userIsConsultant,
        roleClient: ctx.userIsClient
      }
    });
  }

  /**
   * Opens the Add Diagnosis dialog in create mode.
   * @param ownerID required to fetch web sources for the form.
   */
  async addDiagnoses(ctx: LibraryContext, ownerID: string): Promise<void> {
    const { AddDiagnosisComponent } = await import('../components/add-diagnosis/add-diagnosis.component');
    this.dialog_$.open(AddDiagnosisComponent, {
      minHeight: '5vh',
      width: '95vw',
      data: {
        websources: (await this.diagnoses_$.getWebSources(ownerID)).docs.map(doc => doc.data())[0].sources,
        casename: ctx.casename,
        folderId: ctx.currentFolder,
        folderName: ctx.currentFolderName,
        caseFolders: ctx.caseFolders,
        roleConsultant: ctx.userIsConsultant,
        roleClient: ctx.userIsClient
      }
    });
  }

  /**
   * Opens the Show My Diagnoses list dialog.
   * Returns the MatDialogRef so the caller can subscribe to afterClosed()
   * and trigger file refresh (getFilesForPatient is not owned by this facade).
   */
  async showMyDiagnoses(ctx: LibraryContext): Promise<MatDialogRef<any>> {
    const { ShowMyDiagnosesComponent } = await import('../components/show-my-diagnoses/show-my-diagnoses.component');
    return this.dialog_$.open(ShowMyDiagnosesComponent, {
      maxHeight: '85vh',
      width: '50vw',
      data: {
        casename: ctx.casename,
        folderId: ctx.currentFolder,
        folderName: ctx.currentFolderName,
        caseFolders: ctx.caseFolders,
        roleConsultant: ctx.userIsConsultant,
        roleClient: ctx.userIsClient
      }
    });
  }

  /**
   * Deletes a diagnosis.
   */
  deleteDiagnosis(diagnosis: any): void {
    this.diagnoses_$.deleteDiagnosis(diagnosis);
  }

  /**
   * Opens the Add Diagnosis dialog in edit mode.
   */
  async editDiagnosis(diagnosis: any, ctx: LibraryContext): Promise<void> {
    const { AddDiagnosisComponent } = await import('../components/add-diagnosis/add-diagnosis.component');
    this.dialog_$.open(AddDiagnosisComponent, {
      height: 'auto',
      minHeight: '10vh',
      width: '90vw',
      maxWidth: '90vw',
      data: {
        casename: ctx.casename,
        folderId: ctx.currentFolder,
        folderName: ctx.currentFolderName,
        caseFolders: ctx.caseFolders,
        roleConsultant: ctx.userIsConsultant,
        roleClient: ctx.userIsClient,
        diagnosis: diagnosis,
        mode: 'edit'
      }
    });
  }

  /**
   * Fetches and maps diagnoses for a patient into the file-row shape
   * used by the files table.
   */
  async getDiagnoses(casename: string): Promise<any[]> {
    const diagnoses = (await this.diagnoses_$.getDiagnoses(casename)).map(diagnosis => ({
      ...diagnosis,
      expanded: false,
      fileName: diagnosis.diagnosis,
      name: diagnosis.diagnosis,
      type: 'diagnosis',
      icon: 'diagnosis',
      lastModified: new Date((diagnosis.updatedAt?.seconds ?? 0) * 1000).toLocaleDateString('en-US'),
      uploadedDate: new Date((diagnosis.createdAt?.seconds ?? 0) * 1000).toLocaleDateString('en-US')
    }));
    return diagnoses;
  }

  /**
   * Click handler for a diagnosis row — delegates to showDiagnosisDialog.
   */
  openDiagnosis(item: any, $event: MouseEvent): void {
    this.showDiagnosisDialog(item);
  }

  /**
   * Opens the single-diagnosis view dialog.
   */
  async showDiagnosisDialog(item: any): Promise<void> {
    const { ShowMyDiagnosisComponent } = await import('../components/show-my-diagnosis/show-my-diagnosis.component');
    this.dialog_$.open(ShowMyDiagnosisComponent, {
      data: { diagnosis: item }
    });
  }
}
